// ageGroupFormatter.ts

import { PageantAgeGroupBaseModel } from "API/Generated/data-contracts";
import { IAgeGroupValues } from "views/events/add/AddEditEvent";

export interface IPageantAgeGroupBaseModel {
  id: number;
    startAge: number;
    endAge: number;
    startAgeIsMonths: boolean;
    endAgeIsMonths: boolean;
    genderId: number;
    divisionName: string;
    isSupreme: boolean;
    divisionIds: [];
  }

  export interface PageantLevelModel {
    id: number;
    name: string; // adjust if your model uses a different key (e.g. levelName)
  }

  
  export const getAgeGroupRange = (ageGroup: PageantAgeGroupBaseModel): string => {
    console.log('ageGroup:', ageGroup);
  
    if (ageGroup.divisionName) {
      // If there is a division name, display just the division name and 'Boys' if genderId is 2
      return `${ageGroup.divisionName}`;
    } 
    const divisionName = ageGroup?.divisionName;
      // If there is no division name, display the age range startAge-endAge and ' mos or yrs' depending on if startAgeIsMonths or not
      const ageRange = ageGroup.startAge === ageGroup.endAge ? `${ageGroup.startAge}` : `${ageGroup.startAge} - ${ageGroup.endAge}`;
      const ageType = ageGroup.startAgeIsMonths ? ' mos' : ' yrs';
      return `${ageRange}${ageType}${ageGroup.genderId === 2 ? ' Boys' : ''}`;
   
  };

  export const getAgeGroupName = (ageGroup: PageantAgeGroupBaseModel | null | undefined): string => {
    if (!ageGroup) {
      return 'N/A';
    }
  
    const baseName = ageGroup.divisionName || '';
    const ageRange =
      ageGroup.startAge === ageGroup.endAge
        ? `${ageGroup.startAge} ${ageGroup.startAgeIsMonths ? 'mo' : 'yr'}`
        : `${ageGroup.startAge} - ${ageGroup.endAge} ${ageGroup.startAgeIsMonths ? 'mo' : 'yr'}`;
  
    return `${baseName ? baseName + ' ' : ''}${ageRange}${ageGroup.genderId === 2 ? ' Boys' : ''}`;
  };

  // Helper function to get Age Group Name based on ageGroupId
export const getAgeGroupNameById = (ageGroupId: number | null, ageGroups: PageantAgeGroupBaseModel[]): string => {
  if (ageGroupId === null) {
    return 'N/A'; // Return 'N/A' if no ageGroupId is provided
  }

  // Find the age group by id
  const ageGroup = ageGroups.find(group => group.id === ageGroupId);

  if (!ageGroup) {
    return 'N/A'; // Return 'N/A' if no corresponding age group is found
  }

  // Format the name based on the age group data
  const baseName = ageGroup.divisionName || '';
  const ageRange =
    ageGroup.startAge === ageGroup.endAge
      ? `${ageGroup.startAge} ${ageGroup.startAgeIsMonths ? 'mo' : 'yr'}`
      : `${ageGroup.startAge} - ${ageGroup.endAge} ${ageGroup.startAgeIsMonths ? 'mo' : 'yr'}`;

  // Format the final name with gender-specific suffix (e.g., 'Boys')
  return `${baseName ? baseName + ' ' : ''}${ageRange}${ageGroup.genderId === 2 ? ' Boys' : ''}`;
};



export const compareAgeGroups = (a: IPageantAgeGroupBaseModel, b: IPageantAgeGroupBaseModel): number => {
  // Compare by gender first
  const genderComparison = a.genderId - b.genderId;
  
  if (genderComparison !== 0) {
      return genderComparison;
  }

  // If genders are the same, compare by startAge
  const startAgeComparison = a.startAge - b.startAge;

  if (startAgeComparison !== 0) {
      return startAgeComparison;
  }

  // If startAge is the same, compare by endAge
  return a.endAge - b.endAge;
};

export const sortAgeGroups = (ageGroups: IPageantAgeGroupBaseModel[]): IPageantAgeGroupBaseModel[] => {
  // Create a new array to avoid mutating the original
  const sortedAgeGroups = [...ageGroups];

  // Sort the array using the compare function
  sortedAgeGroups.sort(compareAgeGroups);

  return sortedAgeGroups;
};

export const sortByAgeGroups = (ageGroups, contestants) => {
  // Assuming ageGroups is an array of objects like { divisionName, startAge, endAge, genderId }
  const sortedAgeGroups = sortAgeGroups(ageGroups);

  const groupedAgeGroups = {};

  // Group contestants by age group
  contestants.forEach((contestant) => {
    const ageGroupId = contestant.ageGroupId;
    if (ageGroupId) {
      const ageGroupName = getAgeGroupRange(contestant.pageantAgeGroup);

      if (!groupedAgeGroups[ageGroupName]) {
        groupedAgeGroups[ageGroupName] = [];
      }
      groupedAgeGroups[ageGroupName].push(contestant);
    }
  });

  // Separate boys and girls
  const boysAgeGroups = sortedAgeGroups.filter((ageGroup) => ageGroup.divisionName.toLowerCase().includes('boys'));
  const girlsAgeGroups = sortedAgeGroups.filter((ageGroup) => ageGroup.divisionName.toLowerCase().includes('girls'));

  // Sort boys and girls age groups separately
  const sortedBoysAgeGroups = sortAgeGroups(boysAgeGroups);
  const sortedGirlsAgeGroups = sortAgeGroups(girlsAgeGroups);

  // Concatenate sorted age groups
  return sortedBoysAgeGroups.concat(sortedGirlsAgeGroups);
};

export const getDateDiffInDays = (date1: Date, date2: Date): number => {
  const diffInMs = Math.abs(date2.getTime() - date1.getTime());
  return Math.floor(diffInMs / (1000 * 60 * 60 * 24));
};

export const sortAgeGroupsYoungestToOldest = (ageGroups: IPageantAgeGroupBaseModel[] = []): IPageantAgeGroupBaseModel[] => {
  if (!ageGroups || ageGroups.length === 0) {
    return [];
  }

  // Filter out age groups where isSupreme is true
  const nonSupremeGroups = ageGroups.filter((group) => !group.isSupreme);

  // Separate age groups by gender
  const femaleGroups = nonSupremeGroups.filter((group) => group.genderId === 1);
  const maleGroups = nonSupremeGroups.filter((group) => group.genderId === 2);

  // Sort female age groups
  const sortedFemaleGroups = femaleGroups
    .filter((group) => group.startAgeIsMonths) // Filter groups in months
    .sort((a, b) => a.startAge - b.startAge);

  // Sort male age groups
  const sortedMaleGroups = maleGroups
    .filter((group) => group.startAgeIsMonths) // Filter groups in months
    .sort((a, b) => a.startAge - b.startAge);

  // Separate and concatenate age groups in years
  const yearsFemaleGroups = femaleGroups.filter((group) => !group.startAgeIsMonths);
  const yearsMaleGroups = maleGroups.filter((group) => !group.startAgeIsMonths);

  // Concatenate sorted age groups
  const finalSortedAgeGroups = [...sortedFemaleGroups, ...sortedMaleGroups, ...yearsFemaleGroups, ...yearsMaleGroups];

  return finalSortedAgeGroups;
};


export const sortAgeGroupsYoungestToOldest2 = (ageGroups: IPageantAgeGroupBaseModel[] = []): IPageantAgeGroupBaseModel[] => {
  if (!ageGroups || ageGroups.length === 0) {
    return [];
  }

  // Filter out age groups where isSupreme is true
  const nonSupremeGroups = ageGroups.filter((group) => !group.isSupreme);

  // Separate age groups by gender
  const femaleGroups = nonSupremeGroups.filter((group) => group.genderId === 1);
  const maleGroups = nonSupremeGroups.filter((group) => group.genderId === 2);

  // Sort female age groups
  const sortedFemaleGroups = femaleGroups
    .filter((group) => group.startAgeIsMonths) // Filter groups in months
    .sort((a, b) => a.startAge - b.startAge)
    .map((group, index) => ({ ...group, order: index + 1 }));

  // Sort male age groups
  const sortedMaleGroups = maleGroups
    .filter((group) => group.startAgeIsMonths) // Filter groups in months
    .sort((a, b) => a.startAge - b.startAge)
    .map((group, index) => ({ ...group, order: index + 1 }));

  // Separate and concatenate age groups in years
  const yearsFemaleGroups = femaleGroups.filter((group) => !group.startAgeIsMonths);
  const yearsMaleGroups = maleGroups.filter((group) => !group.startAgeIsMonths);

  // Concatenate sorted age groups
  const finalSortedAgeGroups = [
    ...sortedFemaleGroups,
    ...sortedMaleGroups,
    ...yearsFemaleGroups,
    ...yearsMaleGroups,
  ];

  return finalSortedAgeGroups;
};


export const getAgeGroupNameNew = (ageGroup: IAgeGroupValues | null | undefined): string => {
  if (!ageGroup) {
    return 'N/A';
  }

  const baseName = ageGroup.divisionName || '';
  const ageRange =
    ageGroup.startAge === ageGroup.endAge
      ? `${ageGroup.startAge} ${ageGroup.startAgeIsMonths ? 'mo' : 'yr'}`
      : `${ageGroup.startAge} - ${ageGroup.endAge} ${ageGroup.startAgeIsMonths ? 'mo' : 'yr'}`;

  return `${baseName ? baseName + ' ' : ''}`;
};

export const orderAgeGroupsByAge = (ageGroups: IAgeGroupValues[]): IAgeGroupValues[] => {
  return ageGroups
    ?.slice()
    .sort((a, b) => {
      const ageA = a.startAgeIsMonths ? a.startAge! : a.startAge! * 12;
      const ageB = b.startAgeIsMonths ? b.startAge! : b.startAge! * 12;

      return ageA - ageB;
    }) || [];
};


export const orderAgeGroupsByAgeAndSupreme = (ageGroups: IAgeGroupValues[]): IAgeGroupValues[] => {
  return ageGroups
    ?.slice()
    .sort((a, b) => {
      // First, sort by isSupreme (false first, then true)
      if (a.isSupreme !== b.isSupreme) {
        return a.isSupreme ? 1 : -1;
      }

      // Then, sort by age (youngest to oldest)
      const ageA = a.startAgeIsMonths ? a.startAge! : a.startAge! * 12;
      const ageB = b.startAgeIsMonths ? b.startAge! : b.startAge! * 12;

      return ageA - ageB;
    }) || [];
};


